import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { UserPermissions, Role, hasPermission, hasRoleAccess } from '../types/permissions';

interface PermissionContextType {
  permissions: UserPermissions | null;
  loading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
  hasPermission: (permission: string, resourceType?: 'partner' | 'tenant', resourceId?: string) => boolean;
  hasRoleAccess: (requiredRole: Role, resourceType?: 'partner' | 'tenant', resourceId?: string) => boolean;
  getUserRole: (resourceType: 'partner' | 'tenant', resourceId: string) => Role | null;
  isAdmin: boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const userPermissions = await api.getCurrentUser();
      setPermissions(userPermissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
      console.error('Error loading permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (api.isAuthenticated()) {
      refreshPermissions();
    } else {
      setLoading(false);
    }
  }, []);

  const getUserRole = (resourceType: 'partner' | 'tenant', resourceId: string): Role | null => {
    if (!permissions) return null;

    if (resourceType === 'partner') {
      const partnerPermission = permissions.partner_permissions.find(
        p => p.partner_id === resourceId
      );
      return partnerPermission?.role || null;
    } else {
      const tenantPermission = permissions.tenant_permissions.find(
        t => t.tenant_id === resourceId
      );
      return tenantPermission?.role || null;
    }
  };

  const hasPermissionForResource = (permission: string, resourceType?: 'partner' | 'tenant', resourceId?: string): boolean => {
    if (!permissions) return false;
    
    // Global admin has all permissions
    if (permissions.user.is_admin) return true;

    // If no specific resource is specified, check if user has any permissions
    if (!resourceType || !resourceId) {
      return permissions.partner_permissions.length > 0 || permissions.tenant_permissions.length > 0;
    }

    const userRole = getUserRole(resourceType, resourceId);
    if (!userRole) return false;

    return hasPermission(userRole, permission);
  };

  // Enhanced permission check that uses selected tenant if no resource is specified
  const hasPermissionWithTenant = (permission: string, resourceType?: 'partner' | 'tenant', resourceId?: string): boolean => {
    if (!permissions) return false;
    
    // Global admin has all permissions
    if (permissions.user.is_admin) return true;

    // If no specific resource is specified and we have a selected tenant, use it
    if (!resourceType && !resourceId) {
      try {
        const selectedTenant = sessionStorage.getItem('selectedTenant');
        if (selectedTenant) {
          const tenant = JSON.parse(selectedTenant);
          return hasPermissionForResource(permission, 'tenant', tenant.tenant_id);
        }
      } catch (error) {
        console.error('Failed to parse selected tenant:', error);
      }
    }

    return hasPermissionForResource(permission, resourceType, resourceId);
  };

  const hasRoleAccessForResource = (requiredRole: Role, resourceType?: 'partner' | 'tenant', resourceId?: string): boolean => {
    if (!permissions) return false;
    
    // Global admin has all role access
    if (permissions.user.is_admin) return true;

    // If no specific resource is specified, check if user has any admin access
    if (!resourceType || !resourceId) {
      const hasAdminAccess = permissions.partner_permissions.some(p => p.role === 'admin') ||
                            permissions.tenant_permissions.some(t => t.role === 'admin');
      return hasAdminAccess;
    }

    const userRole = getUserRole(resourceType, resourceId);
    if (!userRole) return false;

    return hasRoleAccess(userRole, requiredRole);
  };

  const value: PermissionContextType = {
    permissions,
    loading,
    error,
    refreshPermissions,
    hasPermission: hasPermissionWithTenant,
    hasRoleAccess: hasRoleAccessForResource,
    getUserRole,
    isAdmin: permissions?.user.is_admin || false,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}; 